import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, useTheme, Divider } from "@mui/material";
import { Card, ModalDownloadAppQR, ModalSupport } from '../ui';
import { useSearchParams  } from "react-router-dom";
import Cookies from 'js-cookie';
import { getProfile } from '../../reducers/Profile';
import { isRejected } from '@reduxjs/toolkit';
import { Header, DocumentsList, ProfileTab, DevicesList, SecurityTab, SupportTab } from './components';
import { ReactComponent as AskChatIcon  } from '../../assets/icons/icon_ask_chat.svg'
import { AssignmentOutlined, HelpOutlineOutlined, LockOutlined, PersonOutline, TvOutlined, SmartphoneOutlined } from '@mui/icons-material';
import { isLoading, isSuccess } from '../../constants/redux.constants';
import { ProfileMenuSkeleton } from './components/skeletons';
import Fetch from '../../network/fetch';
import { getUrlParamEncoded } from './utils/getUrlParamEncoded';

const tabs = [
  { id: 'profile',    icon: PersonOutline,        label: 'Мои данные' },
  { id: 'security',   icon: LockOutlined,         label: 'Безопасность' },
  { id: 'documents',  icon: AssignmentOutlined,   label: 'Мои отчеты' },
  { id: 'support',    icon: HelpOutlineOutlined,  label: 'Служба поддержки' },
  { id: 'devices',    icon: TvOutlined,           label: 'Устройства' }
];



const token = getUrlParamEncoded('token');
const server = getUrlParamEncoded('server');


// Ставим на 1 час МП авторизацию
if(token && server) {
  Cookies.set('token', token, { expires: 1/24 }) 
  Cookies.set('server', server, { expires: 1/24 }) 

  // Чистим веб куки, на случай если были
  Cookies.remove("login");
  Cookies.remove("hash");
}

Fetch.init({
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  credentials: 'include'
})


function Profile() {
    document.title = "Д24 | Мои данные";
    const dispatch = useDispatch()


    const [searchParams, setSearchParams] = useSearchParams();

    const { profileLS } = useSelector(state => state.profile)

    const theme = useTheme();

    const tab = getUrlParamEncoded('tab') || 'profile'; 
    const mobileToken = decodeURIComponent(token || '');

    // Модальные окна
    const [modalQR, setModalQR]           = useState(false);
    const [modalSupport, setModalSupport] = useState(false);


    const loadProfile = async () => {
      const result = await dispatch(getProfile());

      if(isRejected(result)) {
        Cookies.remove("login")
        Cookies.remove("hash")
        Cookies.remove("token")
        Cookies.remove("server")
        window.location.replace('https://auth.ds24.ru/')
      }
      
    }

    useEffect(() => {
      loadProfile();
    },[]);

    const handleTabChange = (newTab) => {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('tab', newTab);
      searchParams.has('ref') && newSearchParams.set('ref', searchParams.get('ref'))

      setSearchParams(newSearchParams);
    }

    return (
      <Box sx={{ bgcolor: '#EDEFF0', minHeight: { xs: '103vh', md: '101vh' } }}>
        {!mobileToken && <Header /> }

        <Grid container spacing={{ xs: 2, md: 2 }} p={{ xs: 1, md: 2 }}>
            <Grid item xs={3} display={{ xs: 'none', md: 'grid' }} sx={{ alignSelf: 'flex-start' }} >
              {isLoading(profileLS) && (<ProfileMenuSkeleton />)}
              {isSuccess(profileLS) && (
                <Card sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  minWidth: '100%', 
                  border: `1px solid ${theme.palette.OICO.stone[200]}`,
                  p: { xs: 2, md: 0}
                  }}
                >
                  {tabs.map((tabItem, index) => {
                    const IconComponent = tabItem.icon;
                    const isActive = tab === tabItem.id;
        
                    return (
                      <Box key={tabItem.id}>
                        <Grid 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            p: 2,
                            m: 1,
                            borderRadius: 3,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backgroundColor: isActive ? theme.palette.OICO.purple[100] : 'transparent',
                            '&:hover': {                     
                              backgroundColor: isActive ? theme.palette.OICO.purple[100] : theme.palette.OICO.neutral[100],
                            }
                          }}
                          onClick={() => handleTabChange(tabItem.id)}
                        >
                          <IconComponent sx={{ fill: isActive ? theme.palette.OICO.purple.main : theme.palette.grey.main}} />
                          <Typography sx={{ 
                            fontSize: 14, 
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? theme.palette.OICO.purple.main : 'inherit'
                          }}>
                            {tabItem.label}
                          </Typography>
                        </Grid>
                        {index !== tabs.length - 1 && (
                          <Divider sx={{ bgcolor: theme.palette.OICO.neutral[300],  }} />
                        )}
                      </Box>
                    );
                  })}
                </Card>
              )}
            </Grid>

          <Grid item xs={12} md={9} gap={3}>
            {tab === 'profile'      && <ProfileTab />}
            {tab === 'security'     && <SecurityTab />}
            {tab === 'documents'    && <DocumentsList />}
            {tab === 'support'      && <SupportTab />}
            {tab === 'devices'      && <DevicesList />}
          </Grid>

        {(isSuccess(profileLS)) && (
          <Grid xs={12} md={3} sx={{ position: { xs: 'relative', md: 'fixed' }, bottom: 25, marginLeft: 2, pt: 2, pb: { xs: mobileToken ? 0 : 8, md: 0 } }}>
            <Grid sx={{ pt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                <AskChatIcon style={{ cursor: 'default', flexShrink: 0 }} />
                <Grid sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: theme.palette.grey.main, fontSize: 14 }}>Возникли проблемы?</Typography>
                  <Typography onClick={() => setModalSupport(true)} sx={{ color: theme.palette.blue, fontSize: 14, cursor: 'pointer' }}>Служба поддержки</Typography>
                </Grid>
              </Grid>
              <Grid sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                <SmartphoneOutlined  style={{ cursor: 'default', fill: theme.palette.grey.main }} />
                <Typography onClick={() => setModalQR(true)} sx={{ color: theme.palette.blue, fontSize: 14, cursor: 'pointer' }}>Скачать приложение</Typography>
              </Grid>        
            </Grid> 
          </Grid>
        )}
        </Grid>

        <Grid item xs={3} display={{ xs: mobileToken ? 'none' : 'grid', md: 'none' }} sx={{ alignSelf: 'flex-start', position: 'fixed', bottom: 0, width: '100%' }} >
          {isLoading(profileLS) && (<ProfileMenuSkeleton />)}
          {isSuccess(profileLS) && (
            <Card sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              justifyContent: 'space-around',
              minWidth: '100%', 
              borderRadius: 0,
              border: `1px solid ${theme.palette.OICO.stone[200]}`,
              p: { xs: 0, md: 0},
              borderRight: 0,
              m: 0,
              zIndex: 100
              }}
            >
              {tabs.map((tabItem, index) => {
                const IconComponent = tabItem.icon;
                const isActive = tab === tabItem.id;
    
                return (
                  <Box key={tabItem.id}>
                    <Grid 
                      sx={{ 
                        justifyContent: 'space-around',
                        display: 'flex', 
                        alignItems: 'center',
                        flexDirection: 'row', 
                        gap: 0.5,
                        p: 1.5,
                        m: 1,
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: isActive ? theme.palette.OICO.purple[100] : 'transparent',
                        '&:hover': {                     
                          backgroundColor: isActive ? theme.palette.OICO.purple[100] : theme.palette.OICO.neutral[100],
                        }
                      }}
                      onClick={() => handleTabChange(tabItem.id)}
                    >
                      <IconComponent sx={{ fill: isActive ? theme.palette.OICO.purple.main : theme.palette.grey.main}} />
                    </Grid>
                  </Box>
                );
              })}
            </Card>
          )}
          </Grid>
        

        <ModalDownloadAppQR open={modalQR}      onClose={() => setModalQR(false)} />    
        <ModalSupport       open={modalSupport} onClose={() => setModalSupport(false)} />  
      </Box>
    );
}

export default Profile;